import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Download, Filter, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { adminApi } from "../../api/auth";
import { Input } from "../../components/ui/input";

const fmtDate = (s) => s ? new Date(s.replace(' ', 'T')).toLocaleString("es-AR") : "—";

export default function AdminAudit() {
  const [filters, setFilters] = useState({ event: "", account: "", from: "", to: "", q: "", page: 1, size: 100 });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true); setErr("");
    try { setData(await adminApi.audit(filters)); }
    catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v, page: 1 }));

  return (
    <div className="space-y-4" data-testid="admin-audit">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="ornament"><span>Auditoria</span></div>
          <h1 className="section-title mt-3 text-3xl">Log de eventos</h1>
        </div>
        <a href={adminApi.exportUrl("audit", filters)}
          className="inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider bg-[#d4a843] text-black rounded hover:bg-[#e0b860]"
          data-testid="export-audit-csv">
          <Download className="w-3.5 h-3.5" /> CSV
        </a>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); fetchData(); }}
        className="border border-[#3a3328] bg-[#15110a]/70 rounded-lg p-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <select value={filters.event} onChange={(e) => set("event", e.target.value)}
            className="bg-[#1a1610] border border-[#3a3328] text-white text-sm rounded px-2 py-1.5">
            <option value="">Todos los eventos</option>
            {(data?.events || []).map((ev) => (
              <option key={ev.event} value={ev.event}>{ev.event} ({ev.c})</option>
            ))}
          </select>
          <Input placeholder="account" value={filters.account} onChange={(e) => set("account", e.target.value.toLowerCase())}
            className="bg-[#1a1610] border-[#3a3328] text-white text-sm h-9" />
          <Input type="date" value={filters.from} onChange={(e) => set("from", e.target.value)}
            className="bg-[#1a1610] border-[#3a3328] text-white text-sm h-9" />
          <Input type="date" value={filters.to} onChange={(e) => set("to", e.target.value)}
            className="bg-[#1a1610] border-[#3a3328] text-white text-sm h-9" />
          <Input placeholder="buscar en details / ip / order" value={filters.q} onChange={(e) => set("q", e.target.value)}
            className="bg-[#1a1610] border-[#3a3328] text-white text-sm h-9" />
        </div>
      </form>

      {err && <div className="text-red-400 text-sm border border-red-900/40 p-3 rounded">{err}</div>}

      <div className="border border-[#3a3328] bg-[#15110a]/70 rounded overflow-hidden">
        <div className="max-h-[70vh] overflow-y-auto overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-[#1a1610] z-10">
              <tr className="text-[10px] uppercase text-[#7e7768] border-b border-[#3a3328]">
                <th className="text-left px-3 py-2">Fecha</th>
                <th className="text-left">Evento</th>
                <th className="text-left">Account</th>
                <th className="text-left">Order</th>
                <th className="text-left">IP / Pais</th>
                <th className="text-left">Detalles</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#d4a843] inline" /></td></tr>}
              {!loading && data?.rows?.length === 0 && <tr><td colSpan={6} className="text-center text-[#7e7768] py-6">Sin resultados</td></tr>}
              {!loading && data?.rows?.map((r) => (
                <tr key={r.id} className="border-b border-[#3a3328]/30">
                  <td className="px-3 py-1.5 text-[#b8b3a8] whitespace-nowrap">{fmtDate(r.created_at)}</td>
                  <td className="text-[#d4a843] font-mono">{r.event}</td>
                  <td className="text-white">
                    {r.account ? (
                      <Link to={`/admin/player/${encodeURIComponent(r.account)}`} className="hover:text-[#d4a843]">{r.account}</Link>
                    ) : <span className="text-[#7e7768]">—</span>}
                  </td>
                  <td className="text-[#b8b3a8] font-mono">{r.order_uid || "—"}</td>
                  <td className="text-[#b8b3a8] whitespace-nowrap">{r.ip || "—"} {r.country && <span className="text-[#7e7768]">({r.country})</span>}</td>
                  <td className="text-[#b8b3a8] max-w-md truncate" title={r.details}>{r.details}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data && data.pages > 1 && (
          <div className="flex items-center justify-between px-3 py-2 text-xs border-t border-[#3a3328]">
            <span className="text-[#7e7768]">{data.total} eventos · pagina {data.page} de {data.pages}</span>
            <div className="flex gap-1">
              <button onClick={() => set("page", Math.max(1, filters.page - 1))} disabled={filters.page <= 1}
                className="px-2 py-1 border border-[#3a3328] rounded text-[#b8b3a8] disabled:opacity-40">
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => set("page", Math.min(data.pages, filters.page + 1))} disabled={filters.page >= data.pages}
                className="px-2 py-1 border border-[#3a3328] rounded text-[#b8b3a8] disabled:opacity-40">
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
